import getDisplayName from '@lib/utils/getDisplayName';
import useService from '@modules/ServiceModule/useService';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { WebphoneCallState } from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import { useCallback, useEffect, useMemo } from 'react';
import { useCallDuration } from './useCallDuration';
import { setCallTimerStart, clearCallTimerStart } from './callTimerRegistry';
import useRecording from './useRecording';

/**
 * Derives the call-state props shared by both `FixedCallBar` and
 * `DraggableCallBar` (identity, timers, recording, transfer handlers).
 * Returns `null` when the call isn't actually in progress, mirroring the
 * `isInCall ? … : null` render guard both components previously implemented
 * inline. All hooks below must run unconditionally, so the `isInCall` check
 * happens last, after every hook call.
 */
export function useCallBarProps(callState: WebphoneCallState, show: boolean) {
  const {
    callId,
    direction,
    established,
    hasSession,
    held,
    inTransfer,
    isAudioMuted,
    isVideoMuted,
    progress,
    remoteIdentity,
    terminating,
    remoteCallId,
  } = callState;
  const { userByContactCenterUserId } = useService();
  const isInCall = direction === 'terminating' ? established : hasSession;
  const task = TasksModule.getTaskByCallId(callId);

  const identity = useMemo(() => {
    const name = getDisplayName(
      task?.displayName ?? remoteIdentity,
      userByContactCenterUserId,
    );
    return name;
  }, [task?.displayName, remoteIdentity, userByContactCenterUserId]);

  const { stop, start, formattedTime } = useCallDuration();

  const handleStart = useCallback(() => {
    setCallTimerStart(callId);
    start();
  }, [callId, start]);

  const handleStop = useCallback(() => {
    clearCallTimerStart(callId);
    stop();
  }, [callId, stop]);

  const handleHangup = () => {
    handleStop();
    SoftPhoneModule.callControl.hangup(callId);
  };

  const handleAnswer = () => {
    handleStop();
    SoftPhoneModule.callControl.answer(callId);
  };

  const handleTransfer = useCallback(() => {
    SoftPhoneModule.callControl.hold();
  }, []);

  const {
    isRecording,
    isRecordingDisabled,
    isRecordingLoading,
    recordingTooltip,
    toggleRecording,
  } = useRecording(callId);

  // This useEffect stops the timer when the call ends
  useEffect(() => {
    if (!isInCall) {
      handleStop();
    }
  }, [isInCall, handleStop]);

  if (!isInCall) {
    return null;
  }

  return {
    show,
    established,
    formattedTime,
    hasSession,
    held,
    inTransfer,
    isAudioMuted,
    isVideoMuted,
    phoneConnected: isInCall,
    onHangup: handleHangup,
    onAnswer: handleAnswer,
    onHold: () => SoftPhoneModule.callControl.hold(),
    onMute: () => SoftPhoneModule.callControl.mute(),
    onStartTimer: handleStart,
    onUnhold: () => SoftPhoneModule.callControl.unhold(callId),
    onUnmute: () => SoftPhoneModule.callControl.unmute(),
    progress,
    identity,
    terminating,
    isRecording,
    isRecordingDisabled,
    isRecordingLoading,
    recordingTooltip,
    toggleRecording: () => toggleRecording(remoteCallId),
    onTransfer: handleTransfer,
    task,
    callId,
    remoteCallId,
  };
}

export type CallBarProps = NonNullable<ReturnType<typeof useCallBarProps>>;
